package com.checkin.host.ui.reservations

import androidx.compose.runtime.*
import com.checkin.host.api.ApiClient
import com.checkin.host.api.ApiException
import com.checkin.host.theme.*
import com.checkin.host.ui.components.CollapsibleFilterPanel
import com.checkin.host.ui.components.FilterIconButton
import com.checkin.host.ui.components.FormLabel
import com.checkin.host.ui.components.TextField
import com.checkin.host.ui.components.markSelected
import com.checkin.host.ui.layout.AlertBanner
import com.checkin.host.ui.layout.rememberIsMobile
import com.checkin.host.ui.properties.Badge
import com.checkin.host.util.VerificationBadge
import com.checkin.host.util.reservationVerificationBadge
import com.checkin.host.util.verificationBadgeLabel
import com.checkin.shared.dto.ContractTemplateDto
import com.checkin.shared.dto.CreateReservationRequest
import com.checkin.shared.dto.GuestDto
import com.checkin.shared.dto.PropertyDto
import com.checkin.shared.dto.ReservationDto
import com.checkin.shared.dto.UpdateReservationRequest
import com.checkin.shared.dto.VerificationConfigDto
import com.checkin.shared.dto.VerificationLinkResponse
import com.checkin.shared.model.GuestStatus
import com.checkin.shared.model.ReservationStatus
import kotlinx.browser.window
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.attributes.InputType
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*

@Composable
fun ReservationsScreen() {
    var reservations by remember { mutableStateOf<List<ReservationDto>>(emptyList()) }
    var properties by remember { mutableStateOf<List<PropertyDto>>(emptyList()) }
    var filterPropertyId by remember { mutableStateOf("") }
    var filterStatus by remember { mutableStateOf<ReservationStatus?>(null) }
    var filterCheckInFrom by remember { mutableStateOf("") }
    var filterCheckOutTo by remember { mutableStateOf("") }
    var showForm by remember { mutableStateOf(false) }
    var showFilters by remember { mutableStateOf(false) }
    var editingReservation by remember { mutableStateOf<ReservationDto?>(null) }
    var linkModal by remember { mutableStateOf<LinkModalState?>(null) }
    var mediaModal by remember { mutableStateOf<ReservationDto?>(null) }
    var error by remember { mutableStateOf<String?>(null) }
    var loading by remember { mutableStateOf(true) }
    val scope = rememberCoroutineScope()
    val isMobile = rememberIsMobile()
    val filtersActive = filterPropertyId.isNotBlank() ||
        filterStatus != null ||
        filterCheckInFrom.isNotBlank() ||
        filterCheckOutTo.isNotBlank()

    fun load() {
        scope.launch {
            loading = true
            error = null
            try {
                properties = ApiClient.listProperties()
                reservations = ApiClient.listReservations(
                    propertyId = filterPropertyId.takeIf { it.isNotBlank() },
                    status = filterStatus,
                    checkInFrom = filterCheckInFrom.takeIf { it.isNotBlank() },
                    checkOutTo = filterCheckOutTo.takeIf { it.isNotBlank() },
                )
            } catch (e: ApiException) {
                error = e.message
            } finally {
                loading = false
            }
        }
    }

    LaunchedEffect(Unit) { load() }

    Div({
        style {
            display(DisplayStyle.Flex)
            justifyContent(JustifyContent.SpaceBetween)
            alignItems(AlignItems.Center)
            gap(12.px)
            flexWrap(FlexWrap.Nowrap)
            marginBottom(if (isMobile) 16.px else 24.px)
        }
    }) {
        Div({ style { flex(1); minWidth(0.px); property("min-width", "0") } }) {
            H1({
                style {
                    fontSize(if (isMobile) 22.px else 26.px)
                    fontWeight("700")
                    color(AppColors.text)
                    margin(0.px)
                    letterSpacing((-0.5).px)
                }
            }) { Text("Réservations") }
            P({
                style {
                    color(AppColors.muted)
                    margin(6.px, 0.px, 0.px, 0.px)
                    fontSize(14.px)
                    lineHeight("1.5")
                }
            }) { Text("Gérez les séjours et liens de vérification") }
        }
        Div({
            style {
                display(DisplayStyle.Flex)
                alignItems(AlignItems.Center)
                gap(8.px)
                flexShrink(0)
            }
        }) {
            FilterIconButton(
                expanded = showFilters,
                active = filtersActive,
                onClick = { showFilters = !showFilters },
            )
            Button({
                style {
                    primaryButton()
                    if (isMobile) {
                        fontSize(13.px)
                        padding(8.px, 12.px)
                        whiteSpace("nowrap")
                    }
                }
                onClick {
                    editingReservation = null
                    showForm = !showForm
                }
            }) {
                Text(
                    when {
                        showForm -> "Annuler"
                        isMobile -> "+ Nouvelle"
                        else -> "+ Nouvelle réservation"
                    },
                )
            }
        }
    }
    error?.let { AlertBanner(it, isError = true) }

    linkModal?.let { modal ->
        VerificationLinkModal(modal.reservation, onClose = { linkModal = null })
    }

    mediaModal?.let { reservation ->
        VerificationMediaModal(
            reservation = reservation,
            onClose = { mediaModal = null },
            onUpdated = { load() },
        )
    }

    if (showFilters) {
        CollapsibleFilterPanel(
            isMobile = isMobile,
            applyLabel = "Filtrer",
            onApply = { load() },
            onReset = {
                filterPropertyId = ""
                filterStatus = null
                filterCheckInFrom = ""
                filterCheckOutTo = ""
                load()
            },
        ) {
            Div {
                FormLabel("Hébergement")
                Select(attrs = {
                    style { inputStyle() }
                    onChange { filterPropertyId = it.target.value }
                }) {
                    Option(value = "", attrs = { markSelected(false) }) { Text("Tous") }
                    properties.forEach { p ->
                        Option(value = p.id, attrs = { markSelected(p.id == filterPropertyId) }) { Text(p.name) }
                    }
                }
            }
            Div {
                FormLabel("Statut")
                Select(attrs = {
                    style { inputStyle() }
                    onChange { e ->
                        filterStatus = e.target.value.takeIf { it.isNotEmpty() }
                            ?.let { ReservationStatus.valueOf(it) }
                    }
                }) {
                    Option(value = "", attrs = { markSelected(false) }) { Text("Tous") }
                    ReservationStatus.entries.forEach { s ->
                        Option(value = s.name, attrs = { markSelected(s == filterStatus) }) { Text(s.name.lowercase()) }
                    }
                }
            }
            Div { TextField("Arrivée (depuis)", filterCheckInFrom, InputType.Date) { filterCheckInFrom = it } }
            Div { TextField("Départ (jusqu'à)", filterCheckOutTo, InputType.Date) { filterCheckOutTo = it } }
        }
    }

    if (showForm && editingReservation == null && properties.isNotEmpty()) {
        ReservationForm(
            properties = properties,
            onSaved = { showForm = false; load() },
            onCancel = { showForm = false },
        )
    } else if (showForm && editingReservation == null) {
        AlertBanner("Créez d'abord un hébergement.", isError = true)
    }

    editingReservation?.let { reservation ->
        ReservationForm(
            properties = properties,
            existing = reservation,
            onSaved = { editingReservation = null; load() },
            onCancel = { editingReservation = null },
        )
    }

    if (loading) {
        P({ style { color(AppColors.muted) } }) { Text("Chargement...") }
        return
    }

    if (reservations.isEmpty()) {
        Div({ style { cardStyle() } }) { Text("Aucune réservation.") }
        return
    }

    reservations.forEach { r ->
        ReservationCard(
            reservation = r,
            onEdit = {
                showForm = false
                editingReservation = r
            },
            onOpenLinks = { linkModal = LinkModalState(r) },
            onOpenMedia = { mediaModal = r },
            onDelete = {
                if (window.confirm("Supprimer cette réservation ?")) {
                    scope.launch {
                        try {
                            ApiClient.deleteReservation(r.id)
                            load()
                        } catch (e: ApiException) {
                            error = e.message
                        }
                    }
                }
            },
        )
    }
}

private data class LinkModalState(val reservation: ReservationDto)

@Composable
private fun ReservationCard(
    reservation: ReservationDto,
    onEdit: () -> Unit,
    onOpenLinks: () -> Unit,
    onOpenMedia: () -> Unit,
    onDelete: () -> Unit,
) {
    val isMobile = rememberIsMobile()
    val badge = reservationVerificationBadge(reservation)
    val (badgeBg, badgeColor) = when (badge) {
        VerificationBadge.COMPLETE -> AppColors.successBg to AppColors.success
        VerificationBadge.IN_PROGRESS -> AppColors.infoBg to AppColors.info
        VerificationBadge.MANUAL_REVIEW -> AppColors.reviewBg to AppColors.review
        VerificationBadge.HOST_REVIEW -> AppColors.hostReviewBg to AppColors.hostReview
        VerificationBadge.PENDING -> AppColors.warningBg to AppColors.warning
    }

    Div({ style { cardStyle(); marginBottom(12.px) } }) {
        Div({
            style {
                display(DisplayStyle.Flex)
                justifyContent(JustifyContent.SpaceBetween)
                flexWrap(FlexWrap.Wrap)
                gap(12.px)
                if (isMobile) flexDirection(FlexDirection.Column)
            }
        }) {
            Div {
                H3({ style { margin(0.px, 0.px, 4.px, 0.px) } }) { Text("Réservation ${reservation.referenceManual}") }
                P({ style { margin(0.px); color(AppColors.muted); fontSize(14.px) } }) {
                    Text("${reservation.propertyName} — Hôte: ${reservation.hostName}")
                }
                P({ style { margin(4.px, 0.px, 0.px, 0.px); fontSize(13.px); color(AppColors.muted) } }) {
                    Text("${reservation.checkIn} → ${reservation.checkOut} · ${reservation.guestCount} invité(s)")
                }
            }
            Div({
                style {
                    display(DisplayStyle.Flex)
                    flexDirection(FlexDirection.Column)
                    gap(8.px)
                    if (isMobile) width(100.percent) else alignItems(AlignItems.FlexEnd)
                }
            }) {
                Div({
                    style {
                        display(DisplayStyle.Flex)
                        gap(8.px)
                        flexWrap(FlexWrap.Wrap)
                        if (isMobile) flexDirection(FlexDirection.Column)
                    }
                }) {
                    Badge(verificationBadgeLabel(badge), badgeBg, badgeColor)
                    Button({
                        style {
                            backgroundColor(AppColors.surface)
                            color(AppColors.primary)
                            border(1.px, LineStyle.Solid, AppColors.primary)
                            borderRadius(8.px)
                            padding(8.px, 14.px)
                            cursor("pointer")
                            fontWeight("600")
                            fontSize(13.px)
                            if (isMobile) width(100.percent)
                        }
                        onClick { onEdit() }
                    }) { Text("Modifier") }
                    Button({
                        style {
                            backgroundColor(Color(AppColors.reviewBg))
                            color(AppColors.review)
                            border(1.px, LineStyle.Solid, Color(AppColors.reviewBorder))
                            borderRadius(8.px)
                            padding(8.px, 14.px)
                            cursor("pointer")
                            fontWeight("600")
                            fontSize(13.px)
                            if (isMobile) width(100.percent)
                        }
                        onClick { onOpenLinks() }
                    }) { Text("Lien de vérification") }
                    Button({
                        style {
                            backgroundColor(AppColors.surface)
                            color(AppColors.text)
                            border(1.px, LineStyle.Solid, AppColors.border)
                            borderRadius(8.px)
                            padding(8.px, 14.px)
                            cursor("pointer")
                            fontWeight("600")
                            fontSize(13.px)
                            if (isMobile) width(100.percent)
                        }
                        onClick { onOpenMedia() }
                    }) { Text("Vérifier dossier") }
                }
                Button({
                    style { color(AppColors.danger); backgroundColor(Color.transparent); border(0.px); cursor("pointer"); fontSize(13.px) }
                    onClick { onDelete() }
                }) { Text("Supprimer") }
            }
        }
        if (reservation.guests.isNotEmpty()) {
            Div({
                style {
                    marginTop(12.px)
                    paddingTop(12.px)
                    property("border-top", "1px solid #e5e7eb")
                    fontSize(13.px)
                    color(AppColors.muted)
                }
            }) {
                reservation.guests.forEach { g ->
                    val label = if (g.isPrimary) g.fullName ?: "Invité principal" else "Invité ${g.guestIndex}"
                    Div({ style { marginBottom(4.px) } }) {
                        val manual = if (g.manualVerificationRequired) " · validation nécessaire" else ""
                        Text("$label — ${g.status.name.lowercase()}$manual")
                    }
                }
            }
        }
    }
}

@Composable
private fun VerificationLinkModal(
    reservation: ReservationDto,
    onClose: () -> Unit,
) {
    val primaryName = reservation.guests.firstOrNull { it.isPrimary }?.fullName
    var linkData by remember { mutableStateOf<VerificationLinkResponse?>(null) }
    var loading by remember { mutableStateOf(false) }
    var message by remember { mutableStateOf<String?>(null) }
    var sendingEmail by remember { mutableStateOf(false) }
    val scope = rememberCoroutineScope()
    val isMobile = rememberIsMobile()
    val guestEmail = reservation.primaryGuestEmail?.trim().orEmpty()

    LaunchedEffect(reservation.id) {
        scope.launch {
            loading = true
            message = null
            try {
                linkData = ApiClient.verificationLink(reservation.id)
            } catch (e: ApiException) {
                message = e.message
                linkData = null
            } finally {
                loading = false
            }
        }
    }

    Div({
        style {
            position(Position.Fixed)
            property("inset", "0")
            backgroundColor(Color(AppColors.overlayScrim))
            display(DisplayStyle.Flex)
            alignItems(if (isMobile) AlignItems.FlexEnd else AlignItems.Center)
            justifyContent(JustifyContent.Center)
            property("z-index", "1000")
            padding(if (isMobile) 0.px else 16.px)
        }
        onClick { onClose() }
    }) {
        Div({
            style {
                backgroundColor(AppColors.surface)
                borderRadius(if (isMobile) 16.px else 16.px)
                width(100.percent)
                property("max-width", if (isMobile) "100%" else "420px")
                property("max-height", if (isMobile) "92vh" else "90vh")
                overflow("auto")
            }
            onClick { it.stopPropagation() }
        }) {
            Div({
                style {
                    padding(20.px, 24.px)
                    backgroundColor(AppColors.primary)
                    color(Color.white)
                    textAlign("center")
                }
            }) {
                H2({ style { margin(0.px) } }) { Text("Lien de vérification") }
                P({ style { fontSize(13.px); marginTop(8.px) } }) { Text("Réservation ${reservation.referenceManual}") }
            }
            Div({ style { padding(20.px) } }) {
                P({
                    style {
                        fontSize(13.px)
                        color(AppColors.muted)
                        margin(0.px, 0.px, 16.px, 0.px)
                        lineHeight("1.5")
                    }
                }) {
                    Text(
                        if (reservation.guestCount > 1) {
                            "Envoyez ce lien à l'occupant principal" +
                                (primaryName?.let { " ($it)" } ?: "") +
                                ". Il pourra faire le check-in avec les autres sur place, " +
                                "ou leur transmettre leurs liens personnels. " +
                                "Le code d'accès n'apparaît qu'une fois tous les occupants vérifiés."
                        } else {
                            "Envoyez ce lien à l'occupant pour qu'il complète son check-in."
                        },
                    )
                }
                message?.let { AlertBanner(it, isError = false) }
                if (loading) P({ style { color(AppColors.muted) } }) { Text("Chargement...") }
                linkData?.let { link ->
                    Button({
                        style {
                            width(100.percent)
                            padding(14.px)
                            backgroundColor(Color("#25D366"))
                            color(Color.white)
                            border(0.px)
                            borderRadius(8.px)
                            cursor("pointer")
                            fontWeight("600")
                            marginBottom(10.px)
                        }
                        onClick { window.open(link.whatsAppUrl, "_blank") }
                    }) { Text("Partager via WhatsApp") }
                    if (guestEmail.isNotBlank()) {
                        Button({
                            style {
                                width(100.percent)
                                padding(14.px)
                                backgroundColor(AppColors.primary)
                                color(Color.white)
                                border(0.px)
                                borderRadius(8.px)
                                cursor(if (sendingEmail) "wait" else "pointer")
                                fontWeight("600")
                                marginBottom(10.px)
                                property("opacity", if (sendingEmail) "0.7" else "1")
                            }
                            if (!sendingEmail) {
                                onClick {
                                    scope.launch {
                                        sendingEmail = true
                                        message = null
                                        try {
                                            val response = ApiClient.sendInvitationEmail(reservation.id)
                                            message = response.message
                                        } catch (e: ApiException) {
                                            message = e.message
                                        } finally {
                                            sendingEmail = false
                                        }
                                    }
                                }
                            }
                        }) {
                            Text(if (sendingEmail) "Envoi en cours…" else "Envoyer par email ($guestEmail)")
                        }
                    }
                    Button({
                        style {
                            width(100.percent)
                            padding(14.px)
                            backgroundColor(Color("#ede9fe"))
                            color(AppColors.primary)
                            border(0.px)
                            borderRadius(8.px)
                            cursor("pointer")
                            fontWeight("600")
                            marginBottom(10.px)
                        }
                        onClick {
                            window.navigator.clipboard.writeText(link.url)
                            message = "Lien copié !"
                        }
                    }) { Text("Copier le lien") }
                    P({
                        style {
                            fontSize(12.px)
                            color(AppColors.muted)
                            property("word-break", "break-all")
                            backgroundColor(AppColors.surfaceHigh)
                            padding(8.px)
                            borderRadius(6.px)
                        }
                    }) { Text(link.url) }
                }
                Button({ style { secondaryButton(); width(100.percent); marginTop(12.px) }; onClick { onClose() } }) {
                    Text("Fermer")
                }
            }
        }
    }
}

@Composable
private fun ReservationForm(
    properties: List<PropertyDto>,
    existing: ReservationDto? = null,
    onSaved: () -> Unit,
    onCancel: () -> Unit,
) {
    val isEdit = existing != null
    var propertyId by remember(existing?.id) { mutableStateOf(existing?.propertyId ?: properties.firstOrNull()?.id.orEmpty()) }
    var reference by remember(existing?.id) { mutableStateOf(existing?.referenceManual.orEmpty()) }
    var checkIn by remember(existing?.id) { mutableStateOf(existing?.checkIn.orEmpty()) }
    var checkOut by remember(existing?.id) { mutableStateOf(existing?.checkOut.orEmpty()) }
    var guestCount by remember(existing?.id) { mutableStateOf((existing?.guestCount ?: 1).toString()) }
    var primaryName by remember(existing?.id) {
        mutableStateOf(existing?.guests?.firstOrNull { it.isPrimary }?.fullName.orEmpty())
    }
    var primaryEmail by remember(existing?.id) { mutableStateOf(existing?.primaryGuestEmail.orEmpty()) }
    var accessCode by remember(existing?.id) { mutableStateOf(existing?.accessCode.orEmpty()) }
    var contractTemplateId by remember(existing?.id) { mutableStateOf(existing?.contractTemplateId.orEmpty()) }
    var verificationConfig by remember(existing?.id) {
        mutableStateOf(existing?.verificationConfig ?: VerificationConfigDto())
    }
    var templates by remember { mutableStateOf<List<ContractTemplateDto>>(emptyList()) }
    var formError by remember { mutableStateOf<String?>(null) }
    var saving by remember { mutableStateOf(false) }
    val scope = rememberCoroutineScope()
    val isMobile = rememberIsMobile()
    val guestsInProgress = existing?.guests?.any { it.status != GuestStatus.PENDING } == true

    fun submit() {
        if (saving) return
        when {
            reference.isBlank() -> formError = "Référence requise"
            primaryName.isBlank() -> formError = "Nom invité principal requis"
            !verificationConfig.documentEnabled &&
                !verificationConfig.selfieEnabled &&
                !verificationConfig.contractEnabled -> {
                formError = "Activez au moins une étape de vérification."
            }
            else -> {
                formError = null
                saving = true
                scope.launch {
                    try {
                        if (isEdit) {
                            ApiClient.updateReservation(
                                existing!!.id,
                                UpdateReservationRequest(
                                    referenceManual = reference.trim(),
                                    checkIn = checkIn,
                                    checkOut = checkOut,
                                    guestCount = guestCount.toIntOrNull() ?: 1,
                                    primaryGuestName = primaryName.trim(),
                                    primaryGuestEmail = primaryEmail.trim().takeIf { it.isNotBlank() },
                                    accessCode = accessCode.takeIf { it.isNotBlank() },
                                    contractTemplateId = contractTemplateId.takeIf { it.isNotBlank() },
                                    verificationConfig = verificationConfig,
                                ),
                            )
                        } else {
                            ApiClient.createReservation(
                                CreateReservationRequest(
                                    propertyId = propertyId,
                                    referenceManual = reference.trim(),
                                    checkIn = checkIn,
                                    checkOut = checkOut,
                                    guestCount = guestCount.toIntOrNull() ?: 1,
                                    primaryGuestName = primaryName.trim(),
                                    primaryGuestEmail = primaryEmail.trim().takeIf { it.isNotBlank() },
                                    accessCode = accessCode.takeIf { it.isNotBlank() },
                                    contractTemplateId = contractTemplateId.takeIf { it.isNotBlank() },
                                    verificationConfig = verificationConfig,
                                ),
                            )
                        }
                        onSaved()
                    } catch (e: ApiException) {
                        formError = e.message
                    } finally {
                        saving = false
                    }
                }
            }
        }
    }

    LaunchedEffect(Unit) {
        scope.launch {
            try {
                templates = ApiClient.listContractTemplates()
                if (!isEdit && contractTemplateId.isBlank()) {
                    contractTemplateId = templates.firstOrNull { it.isSystemDefault }?.id
                        ?: templates.firstOrNull()?.id.orEmpty()
                }
            } catch (_: Exception) { }
        }
    }

    Div({ style { cardStyle(); marginBottom(16.px) } }) {
        H3({ style { marginTop(0.px) } }) {
            Text(if (isEdit) "Modifier la réservation" else "Nouvelle réservation")
        }
        formError?.let { AlertBanner(it, isError = true) }

        if (isEdit && guestsInProgress) {
            AlertBanner(
                "Un invité a déjà commencé le check-in. Modifier le nombre d'invités ou le nom principal réinitialisera les liens.",
                isError = false,
            )
        }

        if (isEdit) {
            Div({ style { marginBottom(12.px) } }) {
                TextField("Hébergement", existing?.propertyName.orEmpty(), readOnly = true)
            }
        } else {
            Div({ style { marginBottom(12.px) } }) {
                FormLabel("Hébergement")
                Select(attrs = { style { inputStyle() }; onChange { propertyId = it.target.value } }) {
                    properties.forEach { p ->
                        Option(value = p.id, attrs = { markSelected(p.id == propertyId) }) { Text(p.name) }
                    }
                }
            }
        }

        Div({ style { marginBottom(12.px) } }) { TextField("Référence", reference) { reference = it } }
        Div({
            style {
                display(DisplayStyle.Grid)
                property("grid-template-columns", if (isMobile) "1fr" else "1fr 1fr")
                gap(12.px)
                marginBottom(12.px)
            }
        }) {
            TextField("Check-in", checkIn, InputType.Date) { checkIn = it }
            TextField("Check-out", checkOut, InputType.Date) { checkOut = it }
        }
        Div({ style { marginBottom(12.px) } }) { TextField("Nombre d'invités", guestCount) { guestCount = it } }
        Div({ style { marginBottom(12.px) } }) { TextField("Nom invité principal", primaryName) { primaryName = it } }
        Div({ style { marginBottom(12.px) } }) {
            TextField("Email invité principal (optionnel)", primaryEmail, InputType.Email) { primaryEmail = it }
        }
        Div({ style { marginBottom(12.px) } }) { TextField("Code d'accès", accessCode) { accessCode = it } }
        if (templates.isNotEmpty()) {
            Div({ style { marginBottom(12.px) } }) {
                FormLabel("Modèle de contrat")
                Select(attrs = {
                    style { inputStyle() }
                    onChange { contractTemplateId = it.target.value }
                }) {
                    templates.forEach { t ->
                        Option(value = t.id, attrs = { markSelected(t.id == contractTemplateId) }) {
                            Text(if (t.isSystemDefault) "${t.name} (système)" else t.name)
                        }
                    }
                }
            }
        }

        Div({ style { marginBottom(16.px) } }) {
            VerificationConfigPanel(
                config = verificationConfig,
                onDocumentEnabledChange = { enabled ->
                    verificationConfig = verificationConfig.copy(documentEnabled = enabled)
                },
                onSelfieEnabledChange = { enabled ->
                    verificationConfig = verificationConfig.copy(selfieEnabled = enabled)
                },
                onContractEnabledChange = { enabled ->
                    verificationConfig = verificationConfig.copy(contractEnabled = enabled)
                },
                onCustomMessageChange = { message ->
                    verificationConfig = verificationConfig.copy(
                        customMessage = message.takeIf { it.isNotBlank() },
                    )
                },
            )
        }

        Div({
            style {
                display(DisplayStyle.Flex)
                gap(8.px)
                if (isMobile) flexDirection(FlexDirection.Column)
            }
        }) {
            Button({
                style {
                    primaryButton()
                    if (isMobile) width(100.percent)
                    property("opacity", if (saving) "0.7" else "1")
                    property("cursor", if (saving) "wait" else "pointer")
                }
                onClick { submit() }
            }) {
                Text(
                    when {
                        saving && isEdit -> "Enregistrement…"
                        saving -> "Création…"
                        isEdit -> "Enregistrer"
                        else -> "Créer"
                    },
                )
            }
            Button({
                style { secondaryButton(); if (isMobile) width(100.percent) }
                if (!saving) onClick { onCancel() }
            }) { Text("Annuler") }
        }
    }
}
